import * as React from 'react';
import { Tooltip, tokens } from '@fluentui/react-components';
import { Info16Regular, CheckmarkCircle20Filled } from '@fluentui/react-icons';
import type { WizardStep } from './Wizard';

export interface WizardStepsProps {
  steps: WizardStep[];
  activeKey: string;
  completedByIndex: boolean[];
  onStepClick: (key: string) => void;
  canNavigateTo?: (key: string) => boolean;
  style?: React.CSSProperties;
}

const WizardSteps = ({
  steps,
  activeKey,
  completedByIndex,
  onStepClick,
  canNavigateTo,
  style,
}: WizardStepsProps) => {
  const activeIndex = steps.findIndex((s) => s.key === activeKey);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalM}`,
        ...style,
      }}
    >
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const navBlocked = !!canNavigateTo && !canNavigateTo(step.key);
        const isDisabled = !!step.disabled || navBlocked;
        const isCompleted = !!completedByIndex[index];
        const tooltip = step.disabled
          ? step.disabledTooltip
          : navBlocked
          ? step.lockedTooltip
          : undefined;
        return (
          <React.Fragment key={step.key}>
            <button
              type="button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                gap: tokens.spacingHorizontalS,
                background: 'none',
                border: 'none',
                padding: `${tokens.spacingVerticalXS} 0`,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                onStepClick(step.key);
              }}
              disabled={isDisabled}
              aria-current={isActive ? 'step' : undefined}
            >
              {isCompleted ? (
                <CheckmarkCircle20Filled
                  style={{ color: tokens.colorPaletteGreenBackground3 }}
                />
              ) : (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: `2px solid ${
                      isActive
                        ? tokens.colorBrandStroke1
                        : tokens.colorNeutralStroke1
                    }`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {isActive && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: tokens.colorBrandBackground,
                      }}
                    />
                  )}
                </span>
              )}
              <span
                style={{
                  fontSize: tokens.fontSizeBase300,
                  whiteSpace: 'nowrap',
                  color: isActive
                    ? tokens.colorBrandForeground1
                    : tokens.colorNeutralForeground2,
                  fontWeight: isActive
                    ? tokens.fontWeightSemibold
                    : tokens.fontWeightRegular,
                }}
              >
                {step.label}
              </span>
            </button>
            {tooltip && (
              <Tooltip content={tooltip} relationship="description" withArrow>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginLeft: tokens.spacingHorizontalXS,
                  }}
                  aria-label={tooltip}
                >
                  <Info16Regular
                    style={{ color: tokens.colorNeutralForeground3 }}
                  />
                </button>
              </Tooltip>
            )}
            {index < steps.length - 1 && (
              <div
                style={{
                  flex: '1 1 12px',
                  minWidth: 12,
                  maxWidth: 40,
                  height: 2,
                  backgroundColor: tokens.colorNeutralStroke2,
                  margin: `0 ${tokens.spacingHorizontalS}`,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WizardSteps;
